import React from 'react';

import DataTable from '../shared/data_table';
import LoadFailed from '../shared/load_failed';
import StatusPill from '../shared/status_pill';
import CodeBreaks from '../shared/code_breaks';
import { Ledger, Note, StatusCell, What, Who } from '../shared/cells';
import { fmt_count } from '../format';
import {
  CLOSE_DISABLED_TEXT,
  DECISIONS_EMPTY_TEXT,
  DECISIONS_LOAD_FAILED_TEXT,
  READINESS_LOAD_FAILED_TEXT,
  TIERS_EMPTY_TEXT,
  TIERS_LOAD_FAILED_TEXT
} from './adapters';
import DownloadCsvButton from '../../../util_components/csv_export/download_csv_button';
import { decision_csv_columns, na_csv_name, tier_csv_columns } from './csv_columns';

/*
 * the month summary: the readiness header ("N errors, M must-review open → not ready to close")
 * and the seven tiers with teachers / sessions / ₹ at stake.
 */
const TierSummary = ({
  summary,
  load_failed = false,
  month_label,
  on_select_tier,
  on_close_month,
  on_reopen,
  on_see_decisions,
  run_busy_text = '',
  settle_note = '',
  on_refresh,
  on_retry
}) => {
  const { readiness, tiers } = summary;

  const plural = (n, word) => `${fmt_count(n)} ${n === 1 ? word : `${word}s`}`;
  // Counts as one compact strip: decided work sits beside the open count.
  const counts = (t) => (
    <dl className="tp-na-counts">
      {[
        ['Open', t.open_count],
        ['Acknowledged', t.acknowledged_count],
        ['Resolved', t.resolved_count]
      ].map(([k, v]) => (
        <div key={k} className="tp-na-count" data-count={k}>
          <dt>{k}</dt>
          <dd className={v ? undefined : 'tp-muted'}>{fmt_count(v)}</dd>
        </div>
      ))}
    </dl>
  );
  const view_button = (t, className) =>
    t.can_drill ? (
      <button type="button" className={className} onClick={() => on_select_tier(t.tier)}>
        View issues
      </button>
    ) : null;
  const blocks_pill = (t) =>
    t.blocks_close ? <StatusPill tone="red">Blocks close</StatusPill> : null;

  // List-row pattern: tier · counts · ₹ at stake · status and action. Cards below 660px.
  const columns = [
    {
      key: 'label',
      role: 'identifier',
      label: 'Tier',
      track: { compact: '30%', full: '28%' },
      render: (t) => (
        <Who
          name={
            <button
              type="button"
              className="tp-link tp-na-tier-name"
              onClick={() => on_select_tier(t.tier)}
            >
              {t.label}
            </button>
          }
          meta={[
            plural(t.teachers, 'teacher'),
            plural(t.sessions, 'session'),
            t.auto_cleared_count ? `${fmt_count(t.auto_cleared_count)} auto-cleared` : ''
          ]}
        />
      )
    },
    {
      key: 'counts',
      label: 'Issues',
      render: counts
    },
    {
      key: 'amount_at_stake',
      role: 'amount',
      label: '₹ at stake',
      align: 'right',
      track: { compact: '144px', full: '156px' },
      render: (t) => (
        <span className={t.amount_text === '₹0' ? 'tp-c-amt tp-muted' : 'tp-c-amt'}>
          {t.amount_text}
        </span>
      ),
      card_render: (t) => <Ledger total={{ label: 'At stake', value: t.amount_text }} />
    },
    {
      key: 'blocks_close',
      role: 'status',
      label: 'Status',
      align: 'right',
      track: { compact: '132px', full: '150px' },
      render: (t) => (
        <StatusCell
          chip={blocks_pill(t)}
          actions={<div className="tp-c-acts">{view_button(t, 'tp-btn-out tp-na-act')}</div>}
        />
      ),
      card_render: blocks_pill
    },
    {
      key: 'actions',
      role: 'actions',
      label: 'Actions',
      in_table: false,
      card_render: (t) => view_button(t, 'tp-btn-out')
    }
  ];

  // Decisions: teacher · issue · decision (who, when); the note runs under the row.
  const decision_columns = [
    {
      key: 'teacher_name',
      role: 'identifier',
      label: 'Teacher',
      track: { compact: '26%', full: '24%' },
      render: (d) => (
        <Who
          name={d.teacher_name}
          meta={[
            <span className="tp-mono" title={d.teacher_uuid}>
              {d.teacher_short}
            </span>
          ]}
        />
      )
    },
    {
      key: 'label',
      label: 'Issue',
      render: (d) => (
        <What title={d.label}>
          <div className="tp-na-dec-tags">
            <StatusPill tone={d.tier_tone}>{d.tier_label}</StatusPill>
            <span className="tp-mono tp-na-code">
              <CodeBreaks text={d.issue_code} />
            </span>
          </div>
        </What>
      )
    },
    {
      key: 'action',
      role: 'status',
      label: 'Decision',
      align: 'right',
      track: { compact: '168px', full: '196px' },
      render: (d) => (
        <StatusCell
          chip={<StatusPill tone={d.action_tone}>{d.action_label}</StatusPill>}
          meta={[<div title={d.who_uuid || undefined}>{d.who}</div>, d.when_text]}
        />
      )
    },
    {
      key: 'note',
      label: 'Note',
      line: { from: 'label', bare: true },
      render: (d) => (d.note ? <Note text={d.note} /> : null)
    }
  ];

  return (
    <div>
      {load_failed ? (
        <LoadFailed text={READINESS_LOAD_FAILED_TEXT} on_retry={on_retry} />
      ) : (
        <div className={`tp-banner tp-banner-${readiness.tone}`}>
          <div className="tp-na-headline">
            Month readiness · {month_label} — {readiness.headline}
          </div>
          {readiness.teachers_text ? <div>{readiness.teachers_text}</div> : null}
          {readiness.override_text ? <div>{readiness.override_text}</div> : null}
        </div>
      )}

      {summary.close_enabled ? null : (
        <div className="tp-banner tp-banner-warn" role="status">
          {CLOSE_DISABLED_TEXT}
        </div>
      )}

      {settle_note ? (
        <div className="tp-banner tp-banner-info" role="status">
          {settle_note}{' '}
          <button type="button" className="tp-link" onClick={on_refresh}>
            Refresh
          </button>
        </div>
      ) : null}

      <div className="tp-card">
        <div className="tp-card-hd">
          <span className="tp-card-hd-main">
            <span className="tp-card-hd-title">Issues by tier · {month_label}</span>
            <span className="tp-card-hd-sub">
              {load_failed ? '' : `${fmt_count(summary.total_outstanding)} open or acknowledged`}
              {summary.generated_at ? ` · Data as of ${summary.generated_at}` : ''}
            </span>
          </span>
          {/* one active run per month; off because of OTHER state, so natively disabled. */}
          <span className="tp-card-hd-acts">
            <button
              type="button"
              className="tp-btn-out"
              onClick={on_reopen}
              disabled={!!run_busy_text}
            >
              Reopen a teacher-month…
            </button>
            <button
              type="button"
              className="tp-btn"
              onClick={on_close_month}
              disabled={!!run_busy_text}
            >
              Close month…
            </button>
            {load_failed ? null : (
              <DownloadCsvButton
                columns={tier_csv_columns}
                rows={tiers}
                file_name={() => na_csv_name('tiers', summary.pay_month)}
              />
            )}
          </span>
          {run_busy_text ? <span className="tp-card-hd-note">{run_busy_text}</span> : null}
        </div>
        <DataTable
          columns={columns}
          rows={tiers}
          rowKey={(t) => t.key}
          emptyText={load_failed ? TIERS_LOAD_FAILED_TEXT : TIERS_EMPTY_TEXT}
          card_below={660}
        />
      </div>

      <section className="tp-card" aria-label="Recent Ops decisions">
        <div className="tp-card-hd">
          <span className="tp-card-hd-main">
            <span className="tp-card-hd-title">Recent Ops decisions · {month_label}</span>
            <span className="tp-card-hd-sub">{summary.decisions_count_text}</span>
          </span>
          <span className="tp-card-hd-acts">
            {summary.can_see_all_decisions ? (
              <button type="button" className="tp-link" onClick={on_see_decisions}>
                See all {fmt_count(summary.decisions_total)} Ops decisions
              </button>
            ) : null}
            {load_failed ? null : (
              <DownloadCsvButton
                columns={decision_csv_columns}
                rows={summary.decisions}
                file_name={() => na_csv_name('recent-ops-decisions', summary.pay_month)}
              />
            )}
          </span>
        </div>
        <DataTable
          columns={decision_columns}
          rows={summary.decisions}
          rowKey={(d) => d.key}
          emptyText={load_failed ? DECISIONS_LOAD_FAILED_TEXT : DECISIONS_EMPTY_TEXT}
          card_below={660}
        />
      </section>
    </div>
  );
};

export default TierSummary;
